import React, { useState } from 'react';
import { Topic } from '../types/index.ts';
import { topicApi } from '../services/api.ts';

interface EditTopicModalProps {
  topic: Topic;
  onClose: () => void;
  onTopicUpdated: () => void;
}

const EditTopicModal: React.FC<EditTopicModalProps> = ({ topic, onClose, onTopicUpdated }) => {
  const [title, setTitle] = useState(topic.title);
  const [description, setDescription] = useState(topic.description);
  // Initialize with presenters array
  const [presenterAlias, setPresenterAlias] = useState(
    topic.presenters ? topic.presenters.join(',') : ''
  );
  const [geoRegion, setGeoRegion] = useState(topic.geoRegion || 'NAMER');
  const [recordingUrl, setRecordingUrl] = useState(topic.recordingUrl || '');
  const [builderSpaceUrl, setBuilderSpaceUrl] = useState(topic.builderSpaceUrl || '');
  const [gitlabUrl, setGitlabUrl] = useState(topic.gitlabUrl || '');
  const [sessionType, setSessionType] = useState<'next' | 'future' | 'past'>(topic.sessionType);
  const [estimatedPresentationDate, setEstimatedPresentationDate] = useState(
    topic.estimatedPresentationDate || ''
  );
  const [presentedDate, setPresentedDate] = useState(
    topic.presentedAt ? topic.presentedAt.split('T')[0] : ''
  );
  const [duration, setDuration] = useState(
    topic.duration !== undefined && topic.duration !== null ? String(topic.duration) : ''
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [aliasError, setAliasError] = useState('');
  const [urlError, setUrlError] = useState('');
  const [builderSpaceUrlError, setBuilderSpaceUrlError] = useState('');
  const [gitlabUrlError, setGitlabUrlError] = useState('');

  const validateAlias = (alias: string): string => {
    if (!alias.trim()) {
      return 'Presenter aliases required';
    }
    
    // Split by comma, trim, filter empty
    const aliases = alias.split(',')
      .map(a => a.trim().toLowerCase())
      .filter(a => a.length > 0);
    
    // Check for duplicates
    const unique = [...new Set(aliases)];
    if (unique.length !== aliases.length) {
      return 'Duplicate presenter aliases detected';
    }
    
    // Validate count
    if (unique.length > 10) {
      return 'Maximum 10 presenters allowed';
    }
    
    // Validate each alias
    for (const a of unique) {
      if (a.length > 12) {
        return `Alias "${a}" exceeds 12 characters`;
      }
      if (!/^[a-z]+$/.test(a)) {
        return `Alias "${a}" must contain only lowercase letters`;
      }
    }
    
    return '';
  };

  const validateUrl = (url: string): string => {
    if (!url.trim()) {
      return ''; // URL is optional
    }
    try {
      new URL(url);
      return '';
    } catch {
      return 'Please enter a valid URL';
    }
  };

  const handleSessionTypeChange = (newSessionType: 'next' | 'future' | 'past') => {
    // Clear estimated presentation date when switching away from "future"
    if (sessionType === 'future' && newSessionType !== 'future') {
      setEstimatedPresentationDate('');
    }
    // When switching to "future" from another type, show picker in empty state
    if (sessionType !== 'future' && newSessionType === 'future') {
      setEstimatedPresentationDate('');
    }
    setSessionType(newSessionType);
  };

  const handleAliasBlur = () => {
    const error = validateAlias(presenterAlias);
    setAliasError(error);
  };

  const handleUrlBlur = () => {
    const error = validateUrl(recordingUrl);
    setUrlError(error);
  };

  const handleBuilderSpaceUrlBlur = () => {
    const error = validateUrl(builderSpaceUrl);
    setBuilderSpaceUrlError(error);
  };

  const handleGitlabUrlBlur = () => {
    const error = validateUrl(gitlabUrl);
    setGitlabUrlError(error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const aliasValidationError = validateAlias(presenterAlias);
    const urlValidationError = validateUrl(recordingUrl);
    const builderSpaceUrlValidationError = validateUrl(builderSpaceUrl);
    const gitlabUrlValidationError = validateUrl(gitlabUrl);
    
    if (!title.trim() || !description.trim() || aliasValidationError || urlValidationError || builderSpaceUrlValidationError || gitlabUrlValidationError) {
      setError('All required fields must be valid');
      setAliasError(aliasValidationError);
      setUrlError(urlValidationError);
      setBuilderSpaceUrlError(builderSpaceUrlValidationError);
      setGitlabUrlError(gitlabUrlValidationError);
      return;
    }

    // Validate presentation date for past topics
    if (topic.sessionType === 'past') {
      if (!presentedDate) {
        setError('Please select a presentation date');
        return;
      }
      
      const selectedDate = new Date(presentedDate + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate > today) {
        setError('Presentation date cannot be in the future');
        return;
      }
    }

    setIsSubmitting(true);
    setError('');

    try {
      const updates: any = {
        title,
        description,
        presenterAlias,
        geoRegion,
        recordingUrl,
        builderSpaceUrl,
        gitlabUrl,
        sessionType,
        ...(duration !== '' ? { duration: Number(duration) } : { duration: null })
      };

      // Include estimatedPresentationDate for future topics
      if (sessionType === 'future') {
        updates.estimatedPresentationDate = estimatedPresentationDate || null;
      }

      // Include presentedAt for past topics
      if (topic.sessionType === 'past' && presentedDate) {
        updates.presentedAt = new Date(presentedDate + 'T12:00:00.000Z').toISOString();
      }

      await topicApi.updateTopic(topic.topicId || (topic as any).id, updates);

      onTopicUpdated();
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update topic');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Topic</h2>
        </div>

        <form onSubmit={handleSubmit} className="topic-form">
          <div style={{ fontSize: '12px', color: '#b45309', backgroundColor: '#fef9c3', padding: '8px 12px', borderRadius: '4px', marginBottom: '16px', border: '1px solid #fde68a' }}>
            ⚠️ Do not enter any customer, private, or sensitive information.
          </div>
          <div className="form-group">
            <label htmlFor="title"><span style={{ color: 'red' }}>*</span>Title:</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description"><span style={{ color: 'red' }}>*</span>Description:</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={4}
              maxLength={2000}
            />
          </div>

          <div className="form-group">
            <label htmlFor="presenter"><span style={{ color: 'red' }}>*</span>Presenter Aliases (comma-separated):</label>
            <input
              type="text"
              id="presenter"
              value={presenterAlias}
              onChange={(e) => setPresenterAlias(e.target.value.toLowerCase())}
              onBlur={handleAliasBlur}
              placeholder="e.g., alice,bob,charlie (max 10)"
              required
              maxLength={150}
            />
            {aliasError && <div className="error-message" style={{ fontSize: '14px', marginTop: '4px' }}>{aliasError}</div>}
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Enter aliases separated by commas (no @ symbol). Maximum 10 presenters.
            </div>
          </div>

          {topic.sessionType !== 'past' && (
            <div className="form-group">
              <label htmlFor="geoRegion">Your Geo/Region (optional):</label>
              <select
                id="geoRegion"
                value={geoRegion}
                onChange={(e) => setGeoRegion(e.target.value)}
              >
                <option value="NAMER">NAMER</option>
                <option value="LATAM">LATAM</option>
                <option value="GCR">GCR</option>
                <option value="EMEA">EMEA</option>
                <option value="APJ">APJ</option>
                <option value="Other">Other</option>
              </select>
            </div>
          )}

          {topic.sessionType !== 'past' && (
            <div className="form-group">
              <label htmlFor="duration">Estimated Duration (optional):</label>
              <select
                id="duration"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              >
                <option value="">Unknown</option>
                <option value="5">5 min</option>
                <option value="10">10 min</option>
                <option value="15">15 min</option>
                <option value="20">20 min</option>
                <option value="25">25 min</option>
                <option value="30">30 min</option>
                <option value="45">45 min</option>
                <option value="60">60 min</option>
              </select>
            </div>
          )}

          {topic.sessionType !== 'past' && (
            <div className="form-group">
              <label htmlFor="sessionType">Session:</label>
              <select
                id="sessionType"
                value={sessionType}
                onChange={(e) => handleSessionTypeChange(e.target.value as 'next' | 'future' | 'past')}
                required
              >
                <option value="next">Next Upcoming Session</option>
                <option value="future">Future Session (Date TBD)</option>
              </select>
            </div>
          )}

          {/* Show estimated presentation date picker for future topics */}
          {sessionType === 'future' && topic.sessionType !== 'past' && (
            <div className="form-group">
              <label htmlFor="estimatedPresentationDate">Estimated Presentation Date (optional):</label>
              <input
                id="estimatedPresentationDate"
                type="date"
                value={estimatedPresentationDate}
                onChange={(e) => setEstimatedPresentationDate(e.target.value)}
                style={{
                  width: '180px',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
            </div>
          )}

          {/* Show presentation date field for past topics — above URL fields */}
          {topic.sessionType === 'past' && (
            <div className="form-group">
              <label htmlFor="presented-date"><span style={{ color: 'red' }}>*</span>Presentation Date:</label>
              <input
                id="presented-date"
                type="date"
                value={presentedDate}
                onChange={(e) => setPresentedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                required
                style={{
                  width: '180px',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
            </div>
          )}

          {/* URL fields grouped in a subtle gray box — shown for ALL session types */}
          <div style={{ background: '#f8f8f8', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '12px', marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '10px', fontStyle: 'italic' }}>Optional, can add later</div>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label htmlFor="recordingUrl" style={{ fontSize: '14px' }}>Recording URL (Broadcast or other):</label>
              <input
                type="url"
                id="recordingUrl"
                value={recordingUrl}
                onChange={(e) => setRecordingUrl(e.target.value)}
                onBlur={handleUrlBlur}
                placeholder="https://broadcast.amazon.com/recording"
              />
              {urlError && <div className="error-message" style={{ fontSize: '14px', marginTop: '4px' }}>{urlError}</div>}
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label htmlFor="builderSpaceUrl" style={{ fontSize: '14px' }}>Project Content URL (Scoby or other):</label>
              <input
                type="url"
                id="builderSpaceUrl"
                value={builderSpaceUrl}
                onChange={(e) => setBuilderSpaceUrl(e.target.value)}
                onBlur={handleBuilderSpaceUrlBlur}
                placeholder="https://scoby.apdf.people.aws.dev"
              />
              {builderSpaceUrlError && <div className="error-message" style={{ fontSize: '14px', marginTop: '4px' }}>{builderSpaceUrlError}</div>}
            </div>

            <div className="form-group" style={{ marginBottom: '0' }}>
              <label htmlFor="gitlabUrl" style={{ fontSize: '14px' }}>Code repo URL (Gitlab or other):</label>
              <input
                type="url"
                id="gitlabUrl"
                value={gitlabUrl}
                onChange={(e) => setGitlabUrl(e.target.value)}
                onBlur={handleGitlabUrlBlur}
                placeholder="https://gitlab.aws.dev/project"
              />
              {gitlabUrlError && <div className="error-message" style={{ fontSize: '14px', marginTop: '4px' }}>{gitlabUrlError}</div>}
            </div>
          </div>


          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="submit-button">
              {isSubmitting ? 'Updating...' : 'Update Topic'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTopicModal;
